import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  orderBy,
  or,
} from 'firebase/firestore';
import { db } from '@/config/firebase.config';
import {
  ServiceExchange,
  CreateExchangeData,
  UpdateExchangeData,
  ExchangeStatus,
} from '@/types';
import { COLLECTIONS } from '@/constants';
import { convertFirestoreDoc } from '@/utils';
import { incrementUserExchanges } from './user.service';

/**
 * Create a new service exchange request
 */
export const createExchange = async (data: CreateExchangeData): Promise<string> => {
  try {
    const exchangeData = {
      ...data,
      status: 'pending' as ExchangeStatus,
      requestedAt: serverTimestamp(),
      isRated: false,
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.EXCHANGES), exchangeData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating exchange:', error);
    throw error;
  }
};

/**
 * Get an exchange by ID
 */
export const getExchange = async (exchangeId: string): Promise<ServiceExchange | null> => {
  try {
    const exchangeRef = doc(db, COLLECTIONS.EXCHANGES, exchangeId);
    const exchangeSnap = await getDoc(exchangeRef);

    if (!exchangeSnap.exists()) {
      return null;
    }

    return convertFirestoreDoc<ServiceExchange>({
      id: exchangeSnap.id,
      ...exchangeSnap.data(),
    });
  } catch (error) {
    console.error('Error getting exchange:', error);
    throw error;
  }
};

/**
 * Get all exchanges for a user (as provider or requester)
 */
export const getUserExchanges = async (userId: string): Promise<ServiceExchange[]> => {
  try {
    const exchangesRef = collection(db, COLLECTIONS.EXCHANGES);
    const q = query(
      exchangesRef,
      or(where('providerId', '==', userId), where('requesterId', '==', userId)),
      orderBy('requestedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<ServiceExchange>({
        id: doc.id,
        ...doc.data(),
      })
    );
  } catch (error) {
    console.error('Error getting user exchanges:', error);
    throw error;
  }
};

/**
 * Get incoming requests for a provider
 */
export const getIncomingRequests = async (providerId: string): Promise<ServiceExchange[]> => {
  try {
    const exchangesRef = collection(db, COLLECTIONS.EXCHANGES);
    const q = query(
      exchangesRef,
      where('providerId', '==', providerId),
      where('status', '==', 'pending'),
      orderBy('requestedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<ServiceExchange>({
        id: doc.id,
        ...doc.data(),
      })
    );
  } catch (error) {
    console.error('Error getting incoming requests:', error);
    throw error;
  }
};

/**
 * Get scheduled/active exchanges for a user
 */
export const getScheduledExchanges = async (userId: string): Promise<ServiceExchange[]> => {
  try {
    const exchangesRef = collection(db, COLLECTIONS.EXCHANGES);
    const q = query(
      exchangesRef,
      or(where('providerId', '==', userId), where('requesterId', '==', userId)),
      orderBy('requestedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const exchanges = querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<ServiceExchange>({
        id: doc.id,
        ...doc.data(),
      })
    );

    // Filter for accepted or in-progress exchanges
    return exchanges.filter(
      (exchange) => exchange.status === 'accepted' || exchange.status === 'in_progress'
    );
  } catch (error) {
    console.error('Error getting scheduled exchanges:', error);
    throw error;
  }
};

/**
 * Update an exchange
 */
export const updateExchange = async (
  exchangeId: string,
  data: UpdateExchangeData
): Promise<void> => {
  try {
    const exchangeRef = doc(db, COLLECTIONS.EXCHANGES, exchangeId);

    await updateDoc(exchangeRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating exchange:', error);
    throw error;
  }
};

/**
 * Accept an exchange request
 */
export const acceptExchange = async (exchangeId: string): Promise<void> => {
  try {
    await updateExchange(exchangeId, {
      status: 'accepted',
      acceptedAt: new Date(),
    });
  } catch (error) {
    console.error('Error accepting exchange:', error);
    throw error;
  }
};

/**
 * Decline an exchange request
 */
export const declineExchange = async (exchangeId: string): Promise<void> => {
  try {
    await updateExchange(exchangeId, {
      status: 'declined',
    });
  } catch (error) {
    console.error('Error declining exchange:', error);
    throw error;
  }
};

/**
 * Start an exchange
 */
export const startExchange = async (exchangeId: string): Promise<void> => {
  try {
    await updateExchange(exchangeId, {
      status: 'in_progress',
      startedAt: new Date(),
    });
  } catch (error) {
    console.error('Error starting exchange:', error);
    throw error;
  }
};

/**
 * Complete an exchange
 */
export const completeExchange = async (exchangeId: string): Promise<void> => {
  try {
    const exchange = await getExchange(exchangeId);
    if (!exchange) {
      throw new Error('Exchange not found');
    }

    await updateExchange(exchangeId, {
      status: 'completed',
      completedAt: new Date(),
    });

    // Increment exchange count for both users
    await Promise.all([
      incrementUserExchanges(exchange.providerId),
      incrementUserExchanges(exchange.requesterId),
    ]);
  } catch (error) {
    console.error('Error completing exchange:', error);
    throw error;
  }
};

/**
 * Cancel an exchange
 */
export const cancelExchange = async (
  exchangeId: string,
  cancelledBy: string,
  reason?: string
): Promise<void> => {
  try {
    await updateExchange(exchangeId, {
      status: 'cancelled',
      cancelledAt: new Date(),
      cancelledBy,
      cancellationReason: reason,
    });
  } catch (error) {
    console.error('Error cancelling exchange:', error);
    throw error;
  }
};

/**
 * Get all exchanges (admin only)
 */
export const getAllExchanges = async (): Promise<ServiceExchange[]> => {
  try {
    const exchangesRef = collection(db, COLLECTIONS.EXCHANGES);
    const q = query(exchangesRef, orderBy('requestedAt', 'desc'));

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<ServiceExchange>({
        id: doc.id,
        ...doc.data(),
      })
    );
  } catch (error) {
    console.error('Error getting all exchanges:', error);
    throw error;
  }
};

/**
 * Get exchanges by status
 */
export const getExchangesByStatus = async (
  status: ExchangeStatus
): Promise<ServiceExchange[]> => {
  try {
    const exchangesRef = collection(db, COLLECTIONS.EXCHANGES);
    const q = query(
      exchangesRef,
      where('status', '==', status),
      orderBy('requestedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<ServiceExchange>({
        id: doc.id,
        ...doc.data(),
      })
    );
  } catch (error) {
    console.error('Error getting exchanges by status:', error);
    throw error;
  }
};
